// src/components/Layout/Layout.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import Footer from './Footer'; // Import the Footer component
import { theme as appTheme } from '../../styles/theme'; // Use our defined theme

const AppWrapper = styled.div`
  display: flex;
  /* min-height: 100vh; // MainContentWrapper will handle this */
  background-color: ${({ theme }) => theme.colors.appBg || '#e9ecef'}; // Added appBg from my prev example
`;

const MainContentWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh; // Ensures this wrapper takes full height, pushing footer down
  transition: margin-left ${({ theme }) => theme.transitions.medium};
  
  /* Adjust margin based on sidebar state and screen size */
  /* Assuming theme.sidebarWidthCollapsed is '0px' or a small width for a mini-sidebar */
  margin-left: ${({ isSidebarOpen, theme }) => 
    (isSidebarOpen && window.innerWidth > parseInt(theme.breakpoints.md.replace('px',''))) 
    ? theme.sidebarWidth 
    : (window.innerWidth > parseInt(theme.breakpoints.md.replace('px','')) ? (theme.sidebarWidthCollapsed || '0px') : '0px')};
    // If sidebarWidthCollapsed is not defined, default to 0px on desktop when closed.
    // On mobile, margin is 0 (handled by @media below).

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-left: 0; // Sidebar overlays on mobile, so no margin shift
    width: 100%; // Ensure it takes full width on mobile
  }
`;

const ContentArea = styled.main`
  flex-grow: 1; /* This is key to push the Footer down */
  padding: ${({ theme }) => theme.spacing.xl};
  background-color: ${({ theme }) => theme.colors.background};
  overflow-y: auto; // Ensure content area can scroll if needed

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const Overlay = styled.div`
  display: none; 
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998; // Below sidebar (999), above content
    transition: opacity ${({ theme }) => theme.transitions.short};
    opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  }
`;

const Layout = () => {
  // Initialize sidebar state based on screen width
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => window.innerWidth > parseInt(appTheme.breakpoints.md.replace('px',''))
  );

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      const screenIsLarge = window.innerWidth > parseInt(appTheme.breakpoints.md.replace('px',''));
      // Only automatically open on large screens if it was previously closed due to screen size
      // and not manually closed by the user.
      // For simplicity, let's stick to: always open on large, always closed on small initially.
      // User can then toggle.
      if (screenIsLarge) {
        setIsSidebarOpen(true); 
      } else {
        setIsSidebarOpen(false); 
      }
    };
    
    // Call handler right away so state is updated with initial window size
    // handleResize(); // Already handled by useState initializer

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array means this effect runs once on mount and cleans up on unmount


  return (
    // Ensure ThemeProvider is wrapping your app, usually in App.js or index.js
    // <ThemeProvider theme={appTheme}> 
      <AppWrapper theme={appTheme}> {/* Pass theme explicitly if not using ThemeProvider at root */}
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
        <Overlay 
          isOpen={isSidebarOpen && window.innerWidth <= parseInt(appTheme.breakpoints.md.replace('px',''))} 
          onClick={toggleSidebar} 
          theme={appTheme}
        />
        <MainContentWrapper isSidebarOpen={isSidebarOpen} theme={appTheme}>
          <Navbar toggleSidebar={toggleSidebar} />
          <ContentArea theme={appTheme}>
            <Outlet /> {/* Your page content will be rendered here */}
          </ContentArea>
          <Footer /> {/* Footer is added here */}
        </MainContentWrapper>
      </AppWrapper>
    // </ThemeProvider>
  );
};

export default Layout;